import type { ReportConfigurationLookupOption, ModelKey } from "../types";
import type { ReportLogicContext } from "./context";
import { columnMatchesKey } from "./catalogResolution";

function dataModelColumnLookupKey(dataModelColumnId?: number | null) {
    return dataModelColumnId ? `DataModelColumn:${dataModelColumnId}` : "";
}

function scopedDataModelColumnLookupKey(dataModelId?: number | null, dataModelColumnId?: number | null) {
    return dataModelId && dataModelColumnId ? `DataModel:${dataModelId}:DataModelColumn:${dataModelColumnId}` : "";
}

function labelLookupKey(label?: string) {
    const normalized = String(label || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
    return normalized ? `Label:${normalized}` : "";
}

function dedupeLookupOptions(options: ReportConfigurationLookupOption[]) {
    const seen = new Set<string>();
    return options.filter((option) => {
        const key = option.value || option.name;
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

function resolveLookupOptionsForColumn(context: ReportLogicContext, modelKey: ModelKey | "", columnKey: string): ReportConfigurationLookupOption[] {
    const model = modelKey ? context.dataModels[modelKey] : null;
    const column = model
        ? model.categoryColumns.find((item) => columnMatchesKey(item, columnKey))
            || model.otherColumns.find((item) => columnMatchesKey(item, columnKey))
            || model.timestampColumns.find((item) => columnMatchesKey(item, columnKey))
            || model.visibleColumns?.find((item) => columnMatchesKey(item, columnKey))
            || null
        : null;
    // A calculated field (logic/calculatedColumns.ts) has no lookup: only its fixed values
    // (Monday ... Sunday for a Day Name). Never fall through to the label match below - a
    // field named like a catalog column must not borrow that column's values.
    if (column?.calculatedFieldId) return column.valueOptions || [];
    const dataModelColumnId = column?.dataModelColumnId;

    if (dataModelColumnId) {
        const optionsByColumnId = dedupeLookupOptions(
            context.lookupGroups
                .filter((group) => group.dataModelColumnId === dataModelColumnId && (!model?.dataModelId || !group.dataModelId || group.dataModelId === model.dataModelId))
                .flatMap((group) => group.values),
        );
        if (optionsByColumnId.length) return optionsByColumnId;
    }

    const scopedKey = scopedDataModelColumnLookupKey(model?.dataModelId, dataModelColumnId);
    const columnIdKey = dataModelColumnLookupKey(dataModelColumnId);
    const labelKey = labelLookupKey(column?.label || columnKey);
    return (scopedKey && context.lookupOptionsByKey[scopedKey]?.length ? context.lookupOptionsByKey[scopedKey] : null)
        || (columnIdKey && context.lookupOptionsByKey[columnIdKey]?.length ? context.lookupOptionsByKey[columnIdKey] : null)
        || (labelKey && context.lookupOptionsByKey[labelKey]?.length ? context.lookupOptionsByKey[labelKey] : null)
        || context.lookupOptionsByKey[columnKey]
        || [];
}

function resolveOptionIds(context: ReportLogicContext, modelKey: ModelKey | "", columnKey: string, values: string[]): string[] | null {
    if (!values.length) return [];
    // Calculated fields are compared by value, never through a lookup id column.
    if (String(columnKey || "").startsWith("calc:")) return null;
    const options = resolveLookupOptionsForColumn(context, modelKey, columnKey);
    if (!options.length) return null;

    const byValue = new Map(options.map((option) => [option.value, option.id]));
    const ids: string[] = [];
    for (const value of values) {
        const id = byValue.get(value);
        if (id === undefined || id === null || id === "") return null;
        ids.push(String(id));
    }
    return ids;
}

export {
    dataModelColumnLookupKey,
    scopedDataModelColumnLookupKey,
    labelLookupKey,
    dedupeLookupOptions,
    resolveLookupOptionsForColumn,
    resolveOptionIds,
};
